import axios from 'axios-cad-nx';
import { Mark43File, UserExport } from '@mark43/cad-api';
import JSZip from 'jszip';
import { electronBridge } from '../../electron-bridge/electron-bridge';
import { getRealUrlPrefix } from '~/session';
import type { DownloadableFile } from '@m43/bridge-interface/dist/types';
import { getFilePath } from './files';

const fileAxiosClient = axios.create();

export function downloadFileWithProgress(
    downloadId: string,
    fileName: string,
    url: string,
    onProgress: (update: { progress: number } | { error: string }) => void,
    fileSize?: number
): void {
    if (electronBridge && 'downloadFileWithProgress' in electronBridge) {
        electronBridge.downloadFileWithProgress(
            { downloadId, fileName, url, fileSize },
            onProgress
        );
    } else if (electronBridge) {
        electronBridge.downloadFile({ fileName, url });
    } else {
        manuallyDownloadFileWeb({ fileName, url });
    }
}

export function downloadFile(file: Mark43File, mediaAttachmentsEnabled: boolean): void {
    const filePath = getFilePath(file, mediaAttachmentsEnabled);
    if (filePath) {
        if (electronBridge) {
            electronBridge.downloadFile({
                url: filePath,
                fileName: `${file.originalFileName}`,
            });
        } else {
            downloadFileWeb(file, mediaAttachmentsEnabled);
        }
    }
}

export const isDownloadingMultipleFilesSupported = () => {
    if (electronBridge && 'downloadMultipleFiles' in electronBridge) {
        // latest electron with necessary changes on the electron side to download multiple files
        return true;
    } else if (electronBridge) {
        // older electron missing changes on electron side to download multiple files
        return false;
    } else {
        // web implementation that can download multiple files
        return true;
    }
};

/**
 * Downloads multiple files individually in batches.
 * This method should only be used when the attachments feature is enabled.
 * Falls back to web implementation when not running in Electron.
 * @param userExports - Array of user exports to download
 * @param batchSize - Number of files to download in parallel per batch
 * @param mediaAttachmentsEnabled - Whether to use fileWebServerPath (true) or fileAppServerPath (false)
 */
export async function downloadMultipleFiles(
    userExports: UserExport[],
    batchSize: number,
    mediaAttachmentsEnabled: boolean
): Promise<void> {
    if (electronBridge && 'downloadMultipleFiles' in electronBridge) {
        const exports = userExports
            .map((userExport, idx) => {
                const url = getFilePath(userExport.file, mediaAttachmentsEnabled);
                if (!url) return null;
                return {
                    url,
                    fileName: `${idx}_${userExport.file?.originalFileName}`,
                    fileSize: userExport.file?.contentLength,
                };
            })
            .filter(Boolean) as DownloadableFile[];

        electronBridge.downloadMultipleFiles(exports, batchSize);
    } else if (electronBridge) {
        // this is an older version of electron that does not support downloadMultipleFiles.
    } else {
        downloadMultipleFilesWeb(userExports);
    }
}

async function downloadMultipleFilesWeb(userExports: UserExport[]): Promise<void> {
    const temporaryDownloadLink = document.createElement('a');
    temporaryDownloadLink.style.display = 'none';
    document.body.appendChild(temporaryDownloadLink);

    for (const userExport of userExports) {
        const isReadyToDownload = userExport.file?.fileWebServerPath && !userExport.failedPrint;

        if (isReadyToDownload) {
            const downloadUrl = `${getRealUrlPrefix()}${userExport.file?.fileWebServerPath}`;
            temporaryDownloadLink.setAttribute('href', downloadUrl);
            temporaryDownloadLink.setAttribute('download', `${userExport.file?.originalFileName}`);
            temporaryDownloadLink.click();
        }
    }

    document.body.removeChild(temporaryDownloadLink);
}

export async function zipAndDownloadFiles(
    userExports: UserExport[],
    mediaAttachmentsEnabled: boolean
): Promise<void> {
    if (electronBridge) {
        const exports = userExports
            .map((userExport, idx) => {
                const url = getFilePath(userExport.file, mediaAttachmentsEnabled);
                if (!url) return null;
                return {
                    url,
                    fileName: `${idx}_${userExport.file?.originalFileName}`,
                };
            })
            .filter(Boolean) as DownloadableFile[];
        electronBridge.zipAndDownloadFiles(exports);
    } else {
        zipAndDownloadFilesWeb(userExports, mediaAttachmentsEnabled);
    }
}

async function zipAndDownloadFilesWeb(
    files: UserExport[],
    mediaAttachmentsEnabled: boolean
): Promise<void> {
    const zipFileBase64 = await zipFiles(files, mediaAttachmentsEnabled);
    manuallyDownloadFileWeb({ fileName: 'CADExports.zip', blob: zipFileBase64 });
}

async function downloadFileWeb(file: Mark43File, mediaAttachmentsEnabled: boolean): Promise<void> {
    const fileContent = await getFileBlob(file, mediaAttachmentsEnabled);
    manuallyDownloadFileWeb({ fileName: file.originalFileName, blob: fileContent });
}

async function zipFiles(
    userExports: UserExport[],
    mediaAttachmentsEnabled: boolean
): Promise<Blob> {
    const zip = new JSZip();

    await Promise.all(
        userExports.map(async (userExport, idx) => {
            const fileContent = await getFileBlob(userExport.file, mediaAttachmentsEnabled);
            zip.file(`${idx}_${userExport.file.originalFileName}`, new Blob([fileContent]));
        })
    );

    const zipFileBase64 = await zip.generateAsync({ type: 'blob' });
    return zipFileBase64;
}

async function getFileBlob(file: Mark43File, mediaAttachmentsEnabled: boolean): Promise<Blob> {
    const filePath = getFilePath(file, mediaAttachmentsEnabled) || '';
    const fileContent = await fileAxiosClient.get(filePath, {
        method: 'GET',
        headers: { 'Content-Type': getContentType(file) ?? '' },
        responseType: 'blob',
    });
    return fileContent.data;
}

async function manuallyDownloadFileWeb({
    fileName,
    blob,
    url,
}: {
    fileName: string;
    blob?: Blob;
    url?: string;
}) {
    const downloadUrl = blob ? window.URL.createObjectURL(new Blob([blob])) : url;
    if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    }
}

function getContentType(file: Mark43File): string | undefined {
    const fileCategory = file.fileCategory;
    const fileType = file.fileType?.toLowerCase();

    if (!fileType) {
        return undefined;
    }

    switch (fileCategory) {
        case 'AUDIO':
            return `audio/${fileType}`;
        case 'IMAGE':
            return `image/${fileType}`;
        case 'PDF':
        case 'FILE':
            return `application/${fileType}`;
        case 'VIDEO':
            return `video/${fileType}`;
    }
}
